import { ChartPie } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { OverviewResourceTypesProps } from "./interfaces/interface";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import OverviewCardHeader from "./OverviewCardHeader";
import OverviewEmptyState from "./OverviewEmptyState";

export default function OverviewResourceTypes({
  fromdetails = false,
  resourceType,
  loading,
}: OverviewResourceTypesProps) {
  const totalResourceTypeValue =
    resourceType && resourceType.length > 0
      ? resourceType.reduce((sum, item) => sum + item.value, 0)
      : 0;

  const formattedData = resourceType?.map((item) => ({
    name: item.name,
    value: totalResourceTypeValue > 0
      ? ((item.value / totalResourceTypeValue) * 100).toFixed(2)
      : "0.00",
    color: item.color,
  })) || [];

  return (
    <Card className="overview-card overview-distribution-card h-full">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Resource type"
        description="Distribution of recorded events by resource type"
        icon={ChartPie}
        detail={!loading && resourceType?.length ? `${resourceType.length} types` : undefined}
      />
      <CardContent className="overview-card-content space-y-4">
        {loading ? (
          <div className={fromdetails ? "overview-distribution-layout" : "space-y-4"}>
            <div className="overview-donut flex items-center justify-center h-40">
              <Skeleton className="w-32 h-32 rounded-full" />
            </div>
            <div className="overview-distribution-legend grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="overview-legend-row flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <Skeleton className="w-16 h-3" />
                  </div>
                  <Skeleton className="w-6 h-3" />
                </div>
              ))}
            </div>
          </div>
        ) : resourceType && resourceType.length > 0 ? (
          <div className={fromdetails ? "overview-distribution-layout" : "space-y-4"}>
            <div className="overview-donut h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={resourceType.map((item) => ({ ...item }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={fromdetails ? 72 : 40}
                    outerRadius={fromdetails ? 96 : 80}
                    cornerRadius={fromdetails ? 4 : 0}
                    paddingAngle={2}
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {resourceType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke={fromdetails ? "hsl(var(--card))" : undefined} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperClassName={fromdetails ? "overview-chart-tooltip" : undefined}
                    formatter={(value) => {
                      const numericValue = Number(value);
                      const percentage = totalResourceTypeValue > 0
                        ? ((numericValue / totalResourceTypeValue) * 100).toFixed(2)
                        : "0.00";
                      return [`${percentage}%`, ""];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {fromdetails && (
                <div className="overview-donut-label">
                  <strong>{totalResourceTypeValue.toLocaleString()}</strong>
                  <span>Recorded events</span>
                </div>
              )}
            </div>
            <div className="overview-distribution-legend grid grid-cols-2 gap-2">
              {formattedData.map((item, index) => (
                <div key={index} className="overview-legend-row flex items-center justify-between text-xs">
                  <div className="overview-legend-name flex items-center gap-2">
                    <span className="overview-legend-dot w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="overview-legend-value font-medium">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : fromdetails ? (
          <OverviewEmptyState icon={ChartPie} message="No resource events found in this time range." />
        ) : (
          <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">No data found.</div>
        )}
      </CardContent>
    </Card>
  );
}
